import { useCallback, useEffect, useState } from "react"
import { useAppContext } from "@ciq/shared/hooks/context"
import { fetchIssuesSummary } from "./api"

/**
 * Format issue counts as a readable string.
 * @param {Object} issues - Issue counts object
 * @returns {string} Formatted issue string like "2 Missing, 1 Stale"
 */
export function formatIssues(issues) {
  const parts = []
  if (issues.missing > 0) parts.push(`${issues.missing} Missing`)
  if (issues.stale > 0) parts.push(`${issues.stale} Stale`)
  if (issues.error > 0) parts.push(`${issues.error} Error`)
  if (issues.no_data > 0) parts.push(`${issues.no_data} No Data`)
  return parts.join(", ")
}

/**
 * Summary panel showing all schools with job issues.
 * Collapsed by default, expands to show school list.
 * Clicking a school switches to that school and sets filter to issues.
 *
 * @param {Object} props
 * @param {(filter: string) => void} props.onFilterChange - Callback when filter should change
 */
export function SchoolIssuesSummary({ onFilterChange }) {
  const { setSchool, schools } = useAppContext()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    fetchIssuesSummary(controller.signal)
      .then(responseData => {
        setData(responseData)
        setIsLoading(false)
      })
      .catch(e => {
        if (e.name === "AbortError") return
        if (e.handled) return
        console.error(e)
        setError(true)
        setIsLoading(false)
      })

    return () => controller.abort()
  }, [])

  const handleSchoolClick = useCallback(
    schoolId => {
      const school = schools.find(s => s.id === schoolId)
      if (school) {
        setSchool(school)
        onFilterChange("issues")
      }
    },
    [schools, setSchool, onFilterChange]
  )

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  if (isLoading) {
    return (
      <div className="school-issues-summary school-issues-summary--loading">
        <div className="school-issues-summary__header school-issues-summary__header--static">
          <span className="school-issues-summary__label">Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="school-issues-summary school-issues-summary--error">
        <div className="school-issues-summary__header school-issues-summary__header--static">
          <span className="school-issues-summary__label">Unable to load issues summary</span>
        </div>
      </div>
    )
  }

  const hasIssues = data?.total_schools_with_issues > 0

  // No issues - show static healthy banner (matches semester-group-success styling)
  if (!hasIssues) {
    return (
      <div className="school-issues-summary school-issues-summary--healthy">
        <div className="school-issues-summary__header school-issues-summary__header--static">
          <span className="school-issues-summary__label">All Schools Healthy</span>
        </div>
      </div>
    )
  }

  // Has issues - show collapsible panel (matches semester-group-error styling)
  return (
    <div className="school-issues-summary school-issues-summary--has-issues">
      <button type="button"
        className="school-issues-summary__header"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
      >
        <span className={`school-issues-summary__caret ${isExpanded ? "school-issues-summary__caret--open" : ""}`}>
          &#9656;
        </span>
        <span className="school-issues-summary__label">
          {data.total_schools_with_issues} Schools Have Issues
        </span>
      </button>

      {isExpanded && (
        <div className="school-issues-summary__content">
          <ul className="school-issues-summary__list">
            {data.schools.map(school => (
              <li key={school.school_id} className="school-issues-summary__item">
                <button type="button"
                  className="school-issues-summary__school-button"
                  onClick={() => handleSchoolClick(school.school_id)}
                >
                  <span className="school-issues-summary__school-name">
                    {school.school_name}
                  </span>
                  <span className="school-issues-summary__school-issues">
                    {formatIssues(school.issues)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
